/**
 * The page's ONE AudioContext (2026-09-22, the wake). Constructing an
 * AudioContext opens the output device, and on a real Mac that first
 * construction blocks the main thread for 300 to 470 ms — longer with AirPods
 * or a device switch — whatever the autoplay state, because the cost is paid at
 * CONSTRUCTION and not at `resume()`. A second context on the same page costs a
 * fraction of a millisecond, and resuming one that exists costs a tenth of one.
 *
 * That first construction used to sit on the ball's click: the click's handler
 * blocked 1,684 ms in one measured run and the compositor dropped 202 frames,
 * so the tray, the face and the greeting all landed late. So the page builds
 * its context ONCE, in idle time, long before anyone calls; the Line's playback
 * graph, its analysers and the voice cues all share it, and nothing on the
 * click path opens an audio device.
 *
 * Never closed: a closed context cannot be reopened, and one left idle is
 * silent, shows no tab audio indicator and costs nothing.
 */

let ctx: AudioContext | null = null;
/** A constructor that threw is not tried again. The browser will not change
 *  its mind inside a page, and a retry per call would pay the failure every
 *  time something reached for a context it is never going to get. */
let refused = false;

type Win = Window & {
  webkitAudioContext?: typeof AudioContext;
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
};

/** The page's Web Audio constructor, or null where there is none: the server,
 *  or an engine without the API. The build and the warm read it through here,
 *  so what one of them thinks the page can do the other cannot contradict. */
function audioCtor(): typeof AudioContext | null {
  if (typeof window === "undefined") return null;
  return window.AudioContext ?? (window as Win).webkitAudioContext ?? null;
}

/** The page's context, built on the first ask. Null on the server, in a browser
 *  without Web Audio, and for a browser that refuses to build one. */
export function getSharedAudioContext(): AudioContext | null {
  if (ctx || refused) return ctx;
  const Ctor = audioCtor();
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    refused = true;
  }
  return ctx;
}

/**
 * A silent WAV: 100 ms of 8-bit mono at 8 kHz, 800 real samples all at the
 * 8-bit zero (0x80). Played muted, it is what wakes the audio service, and
 * there is nothing in it to hear.
 *
 * The 44-byte header with an EMPTY data chunk is tidier and is a trap: WebKit
 * never settles `play()` on a zero-length file, so nothing downstream ever
 * runs, and Chromium seek-storms a looped one (38,883 seeks in 800 ms). Real
 * samples cost a kilobyte of base64 and play cleanly in both — Chromium
 * settles in 179 ms with 4 seeks, WebKit in 69 ms with 2.
 */
export const SILENT_WAV_DATA_URL =
  "data:audio/wav;base64,UklGRkQDAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YSADAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==";

/**
 * How long the warm waits for `play()` to say anything at all. A promise that
 * never settles would be a page that never builds its context: WebKit can hold
 * media start in a hidden window, and the desktop shell's main window starts
 * hidden behind the splash.
 */
export const WARM_SETTLE_MS = 5000;

/** Where a page that never goes idle stops waiting. WebKit has no
 *  requestIdleCallback — not Safari, not the desktop shell's WKWebView — so
 *  there this delay IS the schedule, and a 0 would put the build on the first
 *  task after the boot effect, right after first paint. */
const WARM_DELAY_MS = 2000;

/** The element that wakes the audio service. One per page, never in the DOM. */
let waker: HTMLAudioElement | null = null;
/** The warm runs once a page: a second call opens no second element and starts
 *  no second schedule. */
let warming = false;

/** The build itself, where nobody is waiting: idle time, never the boot frame
 *  (the shell has its own work there), never a gesture. */
function buildWhenIdle(then?: () => void): void {
  const w = window as Win;
  const build = () => {
    getSharedAudioContext();
    then?.();
  };
  if (typeof w.requestIdleCallback === "function")
    w.requestIdleCallback(build, { timeout: WARM_DELAY_MS });
  else w.setTimeout(build, WARM_DELAY_MS);
}

/** The waker's work is done the moment the context exists: the service stays
 *  warm on its own (measured at 7.8 ms twenty seconds after the element was
 *  stopped) and the context holds it anyway. */
function stopWaker(): void {
  const el = waker;
  waker = null;
  if (!el) return;
  el.pause();
  el.removeAttribute("src");
}

/**
 * Wake the audio service first, build the context second, and block the main
 * thread for neither.
 *
 * `new AudioContext()` is synchronous and its cost IS the audio service's cold
 * start: 237 ms in a lab page, and in the live trace of this module's first
 * build the idle callback that called it held the main thread for 3,401 ms
 * right after first paint. Taking that off the click was right; landing it on
 * the boot only moved the freeze.
 *
 * A muted silent element wakes the same service asynchronously. Muted media
 * needs no gesture and shows no tab audio indicator, its `play()` resolves in
 * about 330 ms with the main thread free throughout (137 animation frames ran
 * while it started), and the constructor after it costs 3 to 9 ms. So the
 * element plays, and only once it is playing is the context built.
 *
 * The same wake covers the OTHER device-backed thing the click builds: the
 * page's first `new RTCPeerConnection()` costs 115 ms cold on the main thread
 * (WebRTC's own first-use init — 183 ms of a 194 ms click in one staging
 * profile) and 0.8 ms for the second, and after this element has played for
 * 800 ms it costs 2.1 ms. The context and the peer are the two the line
 * constructs on the click, so nothing else needs pre-building.
 *
 * An engine that refuses even muted autoplay leaves the schedule exactly as it
 * was, so nothing is worse than before it.
 *
 * Nothing here resumes: there is no user gesture at boot, so the context is
 * born suspended and the first click brings it up.
 */
export function warmSharedAudioContext(): void {
  if (ctx || refused || warming) return;
  // A page with no Web Audio has no service worth waking: the element would
  // play for a context that is never going to be built.
  if (!audioCtor()) return;
  warming = true;
  const w = window as Win;
  /** The build is scheduled once, by whichever of the three comes first: the
   *  element playing, the engine refusing, or the wait running out. */
  let settled = false;
  const settle = (woke: boolean) => {
    if (settled) return;
    settled = true;
    if (woke) buildWhenIdle(stopWaker);
    else {
      // Nothing woke, so the schedule is all there is.
      stopWaker();
      buildWhenIdle();
    }
  };
  try {
    const el = new Audio(SILENT_WAV_DATA_URL);
    el.muted = true;
    el.loop = true;
    el.preload = "auto";
    waker = el;
    // Never appended: an element plays without being in the document.
    void el.play().then(
      () => settle(true),
      () => settle(false)
    );
    w.setTimeout(() => settle(false), WARM_SETTLE_MS);
  } catch {
    // No Audio to build, or a play() that is not a promise: the same schedule.
    settle(false);
  }
}

/**
 * Bring it up on a gesture. Free on the click path: a context that already
 * exists resumes in a tenth of a millisecond, and a context that is already
 * running is not touched.
 */
export function resumeSharedAudioContext(): void {
  const c = getSharedAudioContext();
  if (c && c.state === "suspended") void c.resume().catch(() => {});
}

/** Tests only: forget the page's context and its warm, so a suite can build
 *  its own. */
export function __resetSharedAudioContext(): void {
  ctx = null;
  refused = false;
  warming = false;
  waker = null;
}
